import React, { useState, useEffect } from 'react';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import Card from './ui/Card';
import Button from './ui/Button';
import Badge from './ui/Badge';
import { supabase } from '../lib/supabase';

interface EdenredPanelProps {
  appointment: any;
  onUpdate: (updated: any) => void;
}

interface EdenredData {
  id?: string;
  appointment_id: string;
  civilite: string;
  nom: string;
  prenom: string;
  email: string;
  tel_mobile: string;
  tel_fixe: string;
  raison_sociale: string;
  siret: string;
  siren: string;
  code_naf21: string;
  rue: string;
  code_postal: string;
  ville: string;
  pays: string;
  effectif_estime: number;
  type_entreprise: string;
  type_marche: string;
  libelle_produit: string;
  libelle_service: string;
  libelle_fonction: string;
  job_title: string;
  type_de_tk: string;
  libelle_campagne: string;
  libelle_evenement: string;
  utm_point_de_contact: string;
  call_summary: string;
  commentaire: string;
  edenred_status: string | null;
  edenred_lead_id: string | null;
  edenred_sent_at: string | null;
  edenred_error_message: string | null;
}

const TYPE_ENTREPRISE_OPTIONS = [
  'AGENCES DE COMMUNICATION',
  'ASSOCIATION',
  'ASSOCIATION SOCIALE',
  'CE',
  'CONSEIL GENERAL',
  'CONSEIL REGIONAL',
  'COS',
  'ENTREPRISE PRIVEE',
  'ENTREPRISE PUBLIQUE',
  'EPCI',
  'ETABLISSEMENT PUBLIC',
  'HOPITAL PUBLIC',
  'MAIRIE',
  'MINISTERE',
  'ORGANISME SOCIAL',
  'PROFESSION LIBERALE',
  'SECTEUR PUBLIC',
  'SECTEUR PUBLIC CIBLE PRIORITAIRE',
  'STE D\'ECONOMIE MIXTE',
  'FRANCHISE',
];

const LIBELLE_PRODUIT_OPTIONS = [
  'Ticket Restaurant',
  'Ticket Service',
  'Ticket CESU',
  'Ticket Service CAP',
  'Cleanway',
  'Domiphone',
  'Solutions PWCE',
  'Ticket Restaurant bénévoles et volontaires',
  'Ticket Kadéos',
  'Domitime',
  'e-Ticket Kadéos',
  'Ticket Fleet Pro',
  'Ticket Kadeos Universel',
  'Carte Ticket Restaurant',
  'Chèques cadeaux étranger',
  'Ticket Kadéos Passeport',
  'Plateforme Stim&Go',
  'Application Stim&Go',
  'Carte cadeau Ticket Kadéos',
  'COMPTE MOBILITE',
  'Kadéos Affilié',
];

const LIBELLE_SERVICE_OPTIONS = [
  'Achat',
  'Administration',
  'Commercial',
  'Communication',
  'Comptabilité',
  'Direction',
  'Finances',
  'Juridique',
  'Marketing',
  'Production',
  'Ressources Humaines',
  'Technique',
];

const LIBELLE_FONCTION_OPTIONS = [
  'Administrateur',
  'Agent Administratif',
  'Assistant',
  'Attaché commercial',
  'Autre',
  'Chargé de Mission',
  'Chef de Produit',
  'Chef de Projet',
  'Chef de Service',
  'Chef d\'établissement',
  'Comptable',
  'Conseiller Général',
  'Conseiller Municipal',
  'Conseiller Régional',
  'DGA',
  'DGS',
  'Directeur',
  'Directeur Adjoint',
  'Elu',
  'Gérant',
  'Maire',
  'Maire Adjoint',
  'Président',
  'Profession libérale',
  'Responsable',
  'Responsable commerciale',
  'Secrétaire',
  'Secrétaire Général',
  'Trésorier',
  'Vice-Président',
];

const TYPE_DE_TK_OPTIONS = [
  'RH',
  'Stim',
  'CE',
  'Revente',
];

const TYPE_MARCHE_OPTIONS = [
  'Privé',
  'Public',
  'Etranger',
];

const CIVILITE_OPTIONS = [
  'Monsieur',
  'Mademoiselle',
  'Madame',
  'M.',
  'Mrx',
];

const UTM_POINT_DE_CONTACT_OPTIONS = [
  'phone',
  'contactform',
  'whatsapp',
  'gatedcontent',
  'newsletter',
];

const EdenredPanel: React.FC<EdenredPanelProps> = ({ appointment, onUpdate }) => {
  const [edenredData, setEdenredData] = useState<EdenredData | null>(null);
  const [formData, setFormData] = useState({
    civilite: 'Monsieur',
    nom: '',
    prenom: '',
    email: '',
    tel_mobile: '',
    tel_fixe: '',
    raison_sociale: '',
    siret: '',
    siren: '',
    code_naf21: '',
    rue: '',
    code_postal: '',
    ville: '',
    pays: 'France',
    effectif_estime: 10,
    type_entreprise: 'ENTREPRISE PRIVEE',
    type_marche: 'Privé',
    libelle_produit: 'Ticket Restaurant',
    libelle_service: 'Achat',
    libelle_fonction: 'Assistant',
    job_title: '',
    type_de_tk: 'RH',
    libelle_campagne: '26TRCHOOK',
    libelle_evenement: '',
    utm_point_de_contact: 'phone',
    call_summary: '',
    commentaire: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadEdenredData();
  }, [appointment.id]);

  const loadEdenredData = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('appointments_edenred')
        .select('*')
        .eq('appointment_id', appointment.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setEdenredData(data);
        setFormData({
          civilite: data.civilite || 'Monsieur',
          nom: data.nom || '',
          prenom: data.prenom || '',
          email: data.email || '',
          tel_mobile: data.tel_mobile || '',
          tel_fixe: data.tel_fixe || '',
          raison_sociale: data.raison_sociale || '',
          siret: data.siret || '',
          siren: data.siren || '',
          code_naf21: data.code_naf21 || '',
          rue: data.rue || '',
          code_postal: data.code_postal || '',
          ville: data.ville || '',
          pays: data.pays || 'France',
          effectif_estime: data.effectif_estime || 10,
          type_entreprise: data.type_entreprise || 'ENTREPRISE PRIVEE',
          type_marche: data.type_marche || 'Privé',
          libelle_produit: data.libelle_produit || 'Ticket Restaurant',
          libelle_service: data.libelle_service || 'Achat',
          libelle_fonction: data.libelle_fonction || 'Assistant',
          job_title: data.job_title || '',
          type_de_tk: data.type_de_tk || 'RH',
          libelle_campagne: data.libelle_campagne || '26TRCHOOK',
          libelle_evenement: data.libelle_evenement || '',
          utm_point_de_contact: data.utm_point_de_contact || 'phone',
          call_summary: data.call_summary || '',
          commentaire: data.commentaire || '',
        });
      } else {
        const nameParts = (appointment.prospect_name || '').split(' ');
        const initialData = {
          civilite: 'Monsieur',
          nom: nameParts.slice(1).join(' ') || '',
          prenom: nameParts[0] || '',
          email: '',
          tel_mobile: appointment.phone || '',
          tel_fixe: '',
          raison_sociale: appointment.company_name || '',
          siret: '',
          siren: '',
          code_naf21: '',
          rue: '',
          code_postal: '',
          ville: '',
          pays: 'France',
          effectif_estime: 10,
          type_entreprise: 'ENTREPRISE PRIVEE',
          type_marche: 'Privé',
          libelle_produit: 'Ticket Restaurant',
          libelle_service: 'Achat',
          libelle_fonction: 'Assistant',
          job_title: '',
          type_de_tk: 'RH',
          libelle_campagne: '26TRCHOOK',
          libelle_evenement: '',
          utm_point_de_contact: 'phone',
          call_summary: '',
          commentaire: '',
        };
        setFormData(initialData);
      }
    } catch (err) {
      console.error('Error loading Edenred data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const saveFormData = async () => {
    setIsSaving(true);
    try {
      if (edenredData?.id) {
        const { error } = await supabase
          .from('appointments_edenred')
          .update({
            ...formData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', edenredData.id);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('appointments_edenred')
          .insert({
            appointment_id: appointment.id,
            ...formData,
          })
          .select()
          .single();

        if (error) throw error;
        if (data) setEdenredData(data);
      }
    } catch (err: any) {
      console.error('Error saving form data:', err);
      setError('Erreur lors de la sauvegarde: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      let recordId = edenredData?.id;

      if (recordId) {
        const { error: updateError } = await supabase
          .from('appointments_edenred')
          .update({
            ...formData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', recordId);

        if (updateError) throw updateError;
      } else {
        const { data: insertData, error: insertError } = await supabase
          .from('appointments_edenred')
          .insert({
            appointment_id: appointment.id,
            ...formData,
          })
          .select()
          .single();

        if (insertError) throw insertError;
        if (!insertData) throw new Error('Failed to create Edenred record');

        recordId = insertData.id;
        setEdenredData(insertData);
      }

      const { data, error } = await supabase.functions.invoke('send-edenred-lead', {
        body: { appointment_edenred_id: recordId }
      });

      if (error) throw error;

      if (data?.success) {
        await loadEdenredData();
        onUpdate({ ...appointment });
      } else {
        setError(data?.error || 'Erreur lors de l\'envoi');
      }
    } catch (err: any) {
      console.error('Error sending to Edenred:', err);
      setError(err.message || 'Erreur réseau');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSent = edenredData?.edenred_status === 'sent';
  const hasError = edenredData?.edenred_status === 'error';

  if (isLoading) {
    return (
      <Card title="Envoi Edenred" className="bg-white/80 backdrop-blur-sm border-gray-200 shadow-lg">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Envoi Edenred" className="bg-white/80 backdrop-blur-sm border-gray-200 shadow-lg">
      <div className="space-y-6">
        {isSent && edenredData && (
          <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-green-900">Envoyé avec succès</p>
              <p className="text-sm text-green-700 mt-1">
                ID CRM: <span className="font-mono">{edenredData.edenred_lead_id}</span>
              </p>
              <p className="text-xs text-green-600 mt-1">
                {edenredData.edenred_sent_at && new Date(edenredData.edenred_sent_at).toLocaleString('fr-FR')}
              </p>
            </div>
            <Badge variant="success">Envoyé</Badge>
          </div>
        )}

        {hasError && edenredData && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-red-900">Erreur d'envoi</p>
              <p className="text-sm text-red-700 mt-1">{edenredData.edenred_error_message}</p>
            </div>
            <Badge variant="error">Erreur</Badge>
          </div>
        )}

        {/* Contact Section */}
        <div className="border-b pb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Civilité *
              </label>
              <select
                value={formData.civilite}
                onChange={(e) => setFormData({ ...formData, civilite: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {CIVILITE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prénom *
              </label>
              <input
                type="text"
                value={formData.prenom}
                onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom *
              </label>
              <input
                type="text"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone mobile *
              </label>
              <input
                type="tel"
                value={formData.tel_mobile}
                onChange={(e) => setFormData({ ...formData, tel_mobile: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone fixe
              </label>
              <input
                type="tel"
                value={formData.tel_fixe}
                onChange={(e) => setFormData({ ...formData, tel_fixe: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        {/* Fonction Section */}
        <div className="border-b pb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Fonction</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service *
              </label>
              <select
                value={formData.libelle_service}
                onChange={(e) => setFormData({ ...formData, libelle_service: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {LIBELLE_SERVICE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fonction *
              </label>
              <select
                value={formData.libelle_fonction}
                onChange={(e) => setFormData({ ...formData, libelle_fonction: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {LIBELLE_FONCTION_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titre du poste
              </label>
              <input
                type="text"
                value={formData.job_title}
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        {/* Entreprise Section */}
        <div className="border-b pb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Entreprise</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Raison sociale *
              </label>
              <input
                type="text"
                value={formData.raison_sociale}
                onChange={(e) => setFormData({ ...formData, raison_sociale: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SIRET *
              </label>
              <input
                type="text"
                value={formData.siret}
                onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SIREN
              </label>
              <input
                type="text"
                value={formData.siren}
                onChange={(e) => setFormData({ ...formData, siren: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type d'entreprise *
              </label>
              <select
                value={formData.type_entreprise}
                onChange={(e) => setFormData({ ...formData, type_entreprise: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {TYPE_ENTREPRISE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de marché *
              </label>
              <select
                value={formData.type_marche}
                onChange={(e) => setFormData({ ...formData, type_marche: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {TYPE_MARCHE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Effectif estimé *
              </label>
              <input
                type="number"
                value={formData.effectif_estime}
                onChange={(e) => setFormData({ ...formData, effectif_estime: parseInt(e.target.value) || 10 })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rue *
              </label>
              <input
                type="text"
                value={formData.rue}
                onChange={(e) => setFormData({ ...formData, rue: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code postal *
              </label>
              <input
                type="text"
                value={formData.code_postal}
                onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ville *
              </label>
              <input
                type="text"
                value={formData.ville}
                onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pays *
              </label>
              <input
                type="text"
                value={formData.pays}
                onChange={(e) => setFormData({ ...formData, pays: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        {/* Produit & Campagne Section */}
        <div className="border-b pb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Produit & Campagne</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Produit *
              </label>
              <select
                value={formData.libelle_produit}
                onChange={(e) => setFormData({ ...formData, libelle_produit: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {LIBELLE_PRODUIT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de ticket *
              </label>
              <select
                value={formData.type_de_tk}
                onChange={(e) => setFormData({ ...formData, type_de_tk: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {TYPE_DE_TK_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Campagne *
              </label>
              <input
                type="text"
                value={formData.libelle_campagne}
                onChange={(e) => setFormData({ ...formData, libelle_campagne: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Événement
              </label>
              <input
                type="text"
                value={formData.libelle_evenement}
                onChange={(e) => setFormData({ ...formData, libelle_evenement: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                placeholder="Ex: Fête des Mères/Pères"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Point de contact UTM *
              </label>
              <select
                value={formData.utm_point_de_contact}
                onChange={(e) => setFormData({ ...formData, utm_point_de_contact: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {UTM_POINT_DE_CONTACT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Notes Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Résumé de l'appel
              </label>
              <textarea
                value={formData.call_summary}
                onChange={(e) => setFormData({ ...formData, call_summary: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                rows={3}
                placeholder="Résumé de la conversation téléphonique..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Commentaire
              </label>
              <textarea
                value={formData.commentaire}
                onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
                onBlur={saveFormData}
                disabled={isSent}
                rows={3}
                placeholder="Informations complémentaires..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="flex justify-end">
          <Button
            onClick={handleSubmit}
            disabled={isSent || isSubmitting}
            icon={<Send className="h-4 w-4" />}
          >
            {isSubmitting ? 'Envoi en cours...' : 'Envoyer à Edenred'}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default EdenredPanel;
